import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "../../components/settings/confirm-dialog";
import { EditableList, type ListItem } from "../../components/settings/editable-list";
import { usePropertyTypes } from "../../hooks/use-api";
import { useCreatePropertyType, useDeletePropertyType, useUpdatePropertyType } from "../../hooks/use-settings";

export function PropertyTypesPage(): React.ReactElement {
  const { data, isLoading, error } = usePropertyTypes();
  const createMutation = useCreatePropertyType();
  const updateMutation = useUpdatePropertyType();
  const deleteMutation = useDeletePropertyType();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const items: ListItem[] = (data?.items ?? []).map((item) => ({ ...item, id: String(item.id) }));

  const handleSave = (item: Partial<ListItem>): void => {
    if (item.id) {
      updateMutation.mutate(
        { id: Number(item.id), name: item.name, isActive: item.isActive },
        {
          onSuccess: () => toast.success("Property type updated"),
          onError: () => toast.error("Failed to update property type"),
        },
      );
    } else {
      createMutation.mutate(
        { name: item.name ?? "" },
        {
          onSuccess: () => toast.success("Property type created"),
          onError: () => toast.error("Failed to create property type"),
        },
      );
    }
  };

  const handleConfirmDelete = (): void => {
    if (!pendingDeleteId) return;
    deleteMutation.mutate(Number(pendingDeleteId), {
      onSuccess: () => toast.success("Property type deactivated"),
      onError: () => toast.error("Failed to deactivate property type"),
    });
    setPendingDeleteId(null);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Property Types</h2>
      <p className="text-sm text-muted-foreground mb-2">
        Manage property type classifications for insurance records. Inactive types are hidden from new records but
        preserved on existing ones.
      </p>
      {isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="property-types-loading">
          Loading…
        </p>
      ) : error ? (
        <p className="text-sm text-destructive" data-testid="property-types-error">
          Failed to load property types.
        </p>
      ) : (
        <EditableList
          items={items}
          entityName="Property Type"
          onSave={handleSave}
          onReorder={() => {}}
          onDelete={setPendingDeleteId}
        />
      )}
      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Deactivate Property Type"
        message="This property type will be hidden from new records but preserved on existing ones. Continue?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
